import React, { useState, useEffect, useRef, useCallback } from "react";
import AdminLayout from "../../components/admin/AdminLayout.jsx";
import { API_BASE, apiFetch } from "../../utils/api";
import "./AdminServices.css";

/* webpack require.context for service images */
let SERVICE_IMAGES = {};
try {
  const ctx = require.context(
    "../../assets/uploads/services",
    false,
    /\.(png|jpe?g|gif|webp|svg)$/i
  );
  SERVICE_IMAGES = ctx.keys().reduce((map, key) => {
    map[key.replace(/^\.\//, "")] = ctx(key);
    return map;
  }, {});
} catch (_) {
  /* running outside CRA — images will fall back to API URL */
}

function serviceImg(filename) {
  if (!filename) return null;
  return SERVICE_IMAGES[filename] ?? `${API_BASE}/uploads/services/${filename}`;
}

/** Strip HTML tags to plain text for table preview */
function stripHtml(html) {
  if (!html) return "";
  return html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

/** Count non-null images for a service row */
function countImages(svc) {
  return [svc.image1, svc.image2, svc.image3, svc.image4, svc.image5]
    .filter(Boolean).length;
}

const IMAGE_KEYS = ["image1", "image2", "image3", "image4", "image5"];

const EMPTY_FORM = {
  title:       "",
  slug:        "",
  description: "",
  active:      1,
  // new file uploads (File objects)
  image1: null,
  image2: null,
  image3: null,
  image4: null,
  image5: null,
};

/* ─────────────────────────────────
   Dot-menu (3 vertical dots)
   ───────────────────────────────── */
function DotMenu({ onView, onEdit, onDelete }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handler(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="asv-dot-menu" ref={ref}>
      <button
        className="asv-dot-menu__trigger"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        title="Actions"
      >
        <span /><span /><span />
      </button>
      {open && (
        <div className="asv-dot-menu__dropdown">
          <button onClick={() => { onView();   setOpen(false); }}>
            <span className="asv-dot-menu__icon">👁</span> View
          </button>
          <button onClick={() => { onEdit();   setOpen(false); }}>
            <span className="asv-dot-menu__icon">✏</span> Edit
          </button>
          <button
            className="asv-dot-menu__delete"
            onClick={() => { onDelete(); setOpen(false); }}
          >
            <span className="asv-dot-menu__icon">🗑</span> Delete
          </button>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────
   Status badge
   ───────────────────────────────── */
function StatusBadge({ active }) {
  const isActive = Number(active) === 1;
  return (
    <span className={`asv-badge ${isActive ? "asv-badge--active" : "asv-badge--inactive"}`}>
      {isActive ? "Active" : "Inactive"}
    </span>
  );
}

/* ─────────────────────────────────
   Rich text toolbar button
   ───────────────────────────────── */
function RichBtn({ title, label, cmd, value }) {
  const handleClick = (e) => {
    e.preventDefault();
    document.execCommand(cmd, false, value || null);
  };
  return (
    <button
      type="button"
      className="asv-rich-toolbar__btn"
      title={title}
      onMouseDown={handleClick}
    >
      {label}
    </button>
  );
}

/* ─────────────────────────────────
   Rich text editor component
   ───────────────────────────────── */
function RichEditor({ value, onChange }) {
  const editorRef = useRef(null);

  /* Initialise content when value prop changes from outside (edit mode) */
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    if (el.innerHTML !== value) {
      el.innerHTML = value || "";
    }
  }, [value]);

  const handleInput = () => {
    if (editorRef.current) onChange(editorRef.current.innerHTML);
  };

  const insertList = (e, ordered) => {
    e.preventDefault();
    document.execCommand(ordered ? "insertOrderedList" : "insertUnorderedList", false, null);
    if (editorRef.current) onChange(editorRef.current.innerHTML);
  };

  return (
    <div className="asv-rich-editor-wrap">
      <div className="asv-rich-toolbar">
        <RichBtn title="Bold"   label={<b>B</b>}  cmd="bold" />
        <RichBtn title="Italic" label={<i>I</i>}  cmd="italic" />
        <RichBtn title="Underline" label={<u>U</u>} cmd="underline" />
        <div className="asv-rich-toolbar__sep" />
        <button
          type="button"
          className="asv-rich-toolbar__btn"
          title="Bullet list"
          onMouseDown={(e) => insertList(e, false)}
        >☰</button>
        <button
          type="button"
          className="asv-rich-toolbar__btn"
          title="Numbered list"
          onMouseDown={(e) => insertList(e, true)}
        >#</button>
        <div className="asv-rich-toolbar__sep" />
        <RichBtn title="Remove formatting" label="✕" cmd="removeFormat" />
      </div>
      <div
        ref={editorRef}
        className="asv-rich-editor"
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
      />
    </div>
  );
}

/* ═══════════════════════════════════
   Single image upload slot
   ═══════════════════════════════════ */
function ImageSlot({ label, preview, onChange, onClear }) {
  return (
    <div className="asv-image-slot">
      <span className="asv-image-slot__label-text">{label}</span>
      <label className="asv-image-upload">
        {preview ? (
          <img src={preview} alt="" className="asv-image-upload__preview" />
        ) : (
          <div className="asv-image-upload__placeholder">
            <span>🖼</span>
            <span>Click to upload</span>
          </div>
        )}
        <input
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={(e) => onChange(e.target.files[0])}
        />
      </label>
      {preview && (
        <button
          type="button"
          className="asv-image-slot__clear"
          onClick={onClear}
        >
          Remove
        </button>
      )}
    </div>
  );
}

/* ═══════════════════════════════════
   VIEW MODAL
   ═══════════════════════════════════ */
function ViewModal({ service, onClose }) {
  if (!service) return null;

  const images = IMAGE_KEYS.map((k) => service[k]).filter(Boolean);

  return (
    <div className="asv-modal-overlay" onClick={onClose}>
      <div className="asv-modal asv-modal--view" onClick={(e) => e.stopPropagation()}>
        <div className="asv-modal__header">
          <div className="asv-modal__title-row">
            <span className="asv-modal__tag">VIEW</span>
            <h2 className="asv-modal__title">{service.title || "Service"}</h2>
          </div>
          <button className="asv-modal__close" onClick={onClose}>✕</button>
        </div>

        <div className="asv-modal__body asv-modal__body--view">

          {/* Images gallery */}
          {images.length > 0 && (
            <div className="asv-view-gallery">
              {images.map((fn, i) => (
                <img
                  key={i}
                  src={serviceImg(fn)}
                  alt={`${service.title} image ${i + 1}`}
                  className="asv-view-gallery__img"
                />
              ))}
            </div>
          )}

          {/* Details grid */}
          <div className="asv-view-grid">
            {[
              ["Title",  service.title  || "—"],
              ["Slug",   service.slug   || "—"],
              ["Status", Number(service.active) === 1 ? "Active" : "Inactive"],
              ["Images", `${images.length} / 5`],
            ].map(([label, val]) => (
              <div key={label} className="asv-view-row">
                <span className="asv-view-row__label">{label}</span>
                <span className="asv-view-row__val">{val}</span>
              </div>
            ))}
          </div>

          {/* Description — rendered HTML */}
          {service.description && (
            <div className="asv-view-description">
              <h4>Description</h4>
              <div
                className="asv-view-description__content"
                dangerouslySetInnerHTML={{ __html: service.description }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════
   CREATE / EDIT MODAL
   ═══════════════════════════════════ */
function FormModal({ mode, service, onClose, onSaved }) {
  const [form,    setForm]    = useState(EMPTY_FORM);
  /* previews: keyed by image1..image5 */
  const [previews, setPreviews] = useState({});
  /* track which existing server images to keep (null = cleared by user) */
  const [existingImgs, setExistingImgs] = useState({});
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState("");

  /* Auto-generate slug from title */
  const slugify = (str) =>
    str.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");

  /* Populate when editing */
  useEffect(() => {
    if (mode === "edit" && service) {
      const existing = {};
      IMAGE_KEYS.forEach((k) => { existing[k] = service[k] || null; });
      setExistingImgs(existing);

      const prevs = {};
      IMAGE_KEYS.forEach((k) => {
        if (service[k]) prevs[k] = serviceImg(service[k]);
      });
      setPreviews(prevs);

      setForm({
        title:       service.title       || "",
        slug:        service.slug        || "",
        description: service.description || "",
        active:      Number(service.active) === 1 ? 1 : 0,
        image1: null, image2: null, image3: null, image4: null, image5: null,
      });
    }
  }, [mode, service]);

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const handleTitleChange = (val) => {
    setForm((f) => ({
      ...f,
      title: val,
      slug:  mode === "create" ? slugify(val) : f.slug,
    }));
  };

  const handleFile = (key, file) => {
    if (!file) return;
    set(key, file);
    const reader = new FileReader();
    reader.onload = (e) =>
      setPreviews((p) => ({ ...p, [key]: e.target.result }));
    reader.readAsDataURL(file);
  };

  const handleClear = (key) => {
    set(key, null);
    setExistingImgs((e) => ({ ...e, [key]: null }));
    setPreviews((p) => ({ ...p, [key]: null }));
  };

  const handleSubmit = async () => {
    setError("");
    if (!form.title.trim()) return setError("Service title is required.");

    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("title",       form.title.trim());
      fd.append("slug",        form.slug.trim() || slugify(form.title.trim()));
      fd.append("description", form.description || "");
      fd.append("active",      form.active);

      /* Append new image files */
      IMAGE_KEYS.forEach((k) => {
        if (form[k]) fd.append(k, form[k]);
      });

      /* Tell server which existing images were cleared */
      if (mode === "edit") {
        IMAGE_KEYS.forEach((k) => {
          if (!form[k] && !existingImgs[k]) {
            fd.append(`clear_${k}`, "1");
          }
        });
      }

      if (mode === "create") {
        await apiFetch("/api/admin/services", { method: "POST", body: fd });
      } else {
        await apiFetch(`/api/admin/services/${service.id}`, { method: "PUT", body: fd });
      }
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="asv-modal-overlay" onClick={onClose}>
      <div className="asv-modal asv-modal--form" onClick={(e) => e.stopPropagation()}>
        <div className="asv-modal__header">
          <div className="asv-modal__title-row">
            <span className="asv-modal__tag">{mode === "create" ? "NEW" : "EDIT"}</span>
            <h2 className="asv-modal__title">
              {mode === "create" ? "Add Service" : "Edit Service"}
            </h2>
          </div>
          <button className="asv-modal__close" onClick={onClose}>✕</button>
        </div>

        <div className="asv-modal__body asv-modal__body--form">
          {error && <div className="asv-modal__error">⚠ {error}</div>}

          {/* ── Section: Service Details ── */}
          <div className="asv-form-section">
            <h3 className="asv-form-section__title">Service Details</h3>
            <div className="asv-form-grid">
              <div className="asv-form-field asv-form-field--full">
                <label>Title <span className="req">*</span></label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  placeholder="e.g. Engine Overhaul"
                />
              </div>
              <div className="asv-form-field asv-form-field--full">
                <label>Slug</label>
                <input
                  type="text"
                  value={form.slug}
                  onChange={(e) => set("slug", e.target.value)}
                  placeholder="e.g. engine-overhaul"
                />
                <p className="asv-form-hint">
                  Auto-generated from title. Used in the URL: <code>/services/engine-overhaul</code>
                </p>
              </div>

              {/* Rich text description */}
              <div className="asv-form-field asv-form-field--full">
                <label>Description</label>
                <RichEditor
                  value={form.description}
                  onChange={(val) => set("description", val)}
                />
                <p className="asv-form-hint">
                  Supports bullet lists, numbered lists, and basic formatting. HTML is saved as-is and rendered on the public site.
                </p>
              </div>
            </div>
          </div>

          {/* ── Section: Images ── */}
          <div className="asv-form-section">
            <h3 className="asv-form-section__title">Service Images (up to 5)</h3>
            <div className="asv-images-grid">
              {IMAGE_KEYS.map((key, i) => (
                <ImageSlot
                  key={key}
                  label={`Image ${i + 1}`}
                  preview={previews[key] || null}
                  onChange={(file) => handleFile(key, file)}
                  onClear={() => handleClear(key)}
                />
              ))}
            </div>
            <p className="asv-form-hint" style={{ marginTop: 12 }}>
              Images are saved to <code>src/assets/uploads/services/</code>. Leave blank to keep existing.
            </p>
          </div>

          {/* ── Section: Options ── */}
          <div className="asv-form-section">
            <h3 className="asv-form-section__title">Options</h3>
            <div className="asv-form-grid asv-form-grid--checks">
              <label className="asv-check">
                <input
                  type="checkbox"
                  checked={Number(form.active) === 1}
                  onChange={(e) => set("active", e.target.checked ? 1 : 0)}
                />
                <span>Active (visible on site)</span>
              </label>
            </div>
          </div>
        </div>

        <div className="asv-modal__footer">
          <button className="asv-btn asv-btn--cancel" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="asv-btn asv-btn--save" onClick={handleSubmit} disabled={saving}>
            {saving ? "Saving…" : mode === "create" ? "Create Service" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════
   DELETE MODAL
   ═══════════════════════════════════ */
function DeleteModal({ service, onClose, onDeleted }) {
  const [deleting, setDeleting] = useState(false);
  const [error,    setError]    = useState("");

  const handleDelete = async () => {
    setDeleting(true);
    setError("");
    try {
      await apiFetch(`/api/admin/services/${service.id}`, { method: "DELETE" });
      onDeleted();
    } catch (err) {
      setError(err.message);
      setDeleting(false);
    }
  };

  return (
    <div className="asv-modal-overlay" onClick={onClose}>
      <div className="asv-modal asv-modal--delete" onClick={(e) => e.stopPropagation()}>
        <div className="asv-delete__icon">🗑</div>
        <h2 className="asv-delete__title">Delete Service?</h2>
        <p className="asv-delete__body">
          You are about to permanently delete{" "}
          <strong>{service?.title || "this service"}</strong>. This will also remove all
          associated images. This action cannot be undone.
        </p>
        {error && <div className="asv-modal__error">⚠ {error}</div>}
        <div className="asv-delete__actions">
          <button className="asv-btn asv-btn--cancel" onClick={onClose} disabled={deleting}>
            Cancel
          </button>
          <button className="asv-btn asv-btn--delete" onClick={handleDelete} disabled={deleting}>
            {deleting ? "Deleting…" : "Yes, Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════ */
export default function AdminServices() {
  const [services,   setServices]   = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, pages: 1 });
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState("");

  /* Filters */
  const [search,      setSearch]      = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sort,        setSort]        = useState("newest");
  const [page,        setPage]        = useState(1);
  const searchTimer = useRef(null);

  /* Modals */
  const [modal,  setModal]  = useState(null);
  const [target, setTarget] = useState(null);

  /* ── Load services ── */
  const loadServices = useCallback(() => {
    setLoading(true);
    setError("");
    const q = new URLSearchParams();
    if (search)       q.set("search", search);
    if (statusFilter) q.set("active", statusFilter);
    q.set("sort",  sort);
    q.set("page",  page);
    q.set("limit", "15");

    apiFetch(`/api/admin/services?${q}`)
      .then((data) => {
        setServices(data.services    || []);
        setPagination(data.pagination || { page: 1, total: 0, pages: 1 });
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [search, statusFilter, sort, page]);

  useEffect(() => { loadServices(); }, [loadServices]);

  /* ── Handlers ── */
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setPage(1), 400);
  };

  const openModal  = (type, svc = null) => { setModal(type); setTarget(svc); };
  const closeModal = () => { setModal(null); setTarget(null); };
  const afterSave  = () => { closeModal(); loadServices(); };

  return (
    <AdminLayout pageTag="CONTENT" pageTitle="Services">

      {error && (
        <div className="asv-error-bar">⚠ {error}</div>
      )}

      {/* ── Toolbar ── */}
      <div className="asv-toolbar">
        <div className="asv-toolbar__left">
          <div className="asv-search-wrap">
            <span className="asv-search-wrap__icon">🔍</span>
            <input
              className="asv-search"
              type="text"
              placeholder="Search services by title…"
              value={search}
              onChange={handleSearchChange}
            />
            {search && (
              <button className="asv-search-wrap__clear" onClick={() => { setSearch(""); setPage(1); }}>
                ✕
              </button>
            )}
          </div>

          <select
            className="asv-filter-select"
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          >
            <option value="">All Status</option>
            <option value="1">Active</option>
            <option value="0">Inactive</option>
          </select>

          <select
            className="asv-filter-select"
            value={sort}
            onChange={(e) => { setSort(e.target.value); setPage(1); }}
          >
            <option value="newest">Newest</option>
            <option value="oldest">Oldest</option>
            <option value="title_asc">Title A–Z</option>
          </select>
        </div>

        <div className="asv-toolbar__right">
          <span className="asv-toolbar__count">
            {loading
              ? "…"
              : `${pagination.total} service${pagination.total !== 1 ? "s" : ""}`}
          </span>
          <button className="asv-btn-primary" onClick={() => openModal("create")}>
            + Add Service
          </button>
        </div>
      </div>

      {/* ── Table ── */}
      {loading ? (
        <div className="asv-table-skeleton" />
      ) : services.length === 0 ? (
        <div className="asv-empty">
          <span>🔧</span>
          <p>No services found.</p>
          {(search || statusFilter) && (
            <button
              className="asv-btn asv-btn--cancel"
              onClick={() => { setSearch(""); setStatusFilter(""); setPage(1); }}
            >
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <div className="asv-table-wrap">
          <table className="asv-table">
            <thead>
              <tr>
                <th>Image</th>
                <th>Title / Slug</th>
                <th>Description</th>
                <th>Images</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {services.map((svc) => (
                <tr key={svc.id}>
                  <td>
                    {svc.image1 ? (
                      <img
                        src={serviceImg(svc.image1)}
                        alt=""
                        className="asv-img-thumb"
                      />
                    ) : (
                      <span className="asv-no-img">—</span>
                    )}
                  </td>
                  <td>
                    <div className="asv-title-cell">
                      <span className="asv-title-cell__name">{svc.title || "—"}</span>
                      {svc.slug && (
                        <span className="asv-title-cell__slug">/{svc.slug}</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className="asv-desc-preview">
                      {svc.description ? stripHtml(svc.description).slice(0, 120) + (stripHtml(svc.description).length > 120 ? "…" : "") : "—"}
                    </span>
                  </td>
                  <td>
                    <span className="asv-img-count">
                      🖼 {countImages(svc)} / 5
                    </span>
                  </td>
                  <td>
                    <StatusBadge active={svc.active} />
                  </td>
                  <td>
                    <DotMenu
                      onView={()   => openModal("view",   svc)}
                      onEdit={()   => openModal("edit",   svc)}
                      onDelete={() => openModal("delete", svc)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Pagination ── */}
      {pagination.pages > 1 && (
        <div className="asv-pagination">
          <button
            className="asv-pagination__btn"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >← Prev</button>
          {Array.from({ length: pagination.pages }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              className={`asv-pagination__btn ${p === page ? "active" : ""}`}
              onClick={() => setPage(p)}
            >{p}</button>
          ))}
          <button
            className="asv-pagination__btn"
            disabled={page >= pagination.pages}
            onClick={() => setPage((p) => p + 1)}
          >Next →</button>
        </div>
      )}

      {/* ── Modals ── */}
      {modal === "view"   && <ViewModal   service={target}  onClose={closeModal} />}
      {modal === "create" && <FormModal   mode="create"     onClose={closeModal} onSaved={afterSave} />}
      {modal === "edit"   && <FormModal   mode="edit"       service={target} onClose={closeModal} onSaved={afterSave} />}
      {modal === "delete" && <DeleteModal service={target}  onClose={closeModal} onDeleted={afterSave} />}

    </AdminLayout>
  );
}